interface LogoProps {
  /** Glyph dimension in px. Renders square. */
  size?: number;
  /** CSS color value for the stroke + inner fill. Defaults to `currentColor` so the
   * SVG inherits its parent's color — that's MUI's `text.primary` at the root, which
   * flips to white under dark mode automatically. Override (e.g. with a sage hex) when
   * you want the brand glyph to read against a tinted backdrop. */
  color?: string;
}

// Brand mark — the rotational "صاحب" glyph from the v1 reference logo.
// `aria-hidden` because the wrapping button or wordmark carries the accessible name.
export function Logo({ size = 56, color = 'currentColor' }: LogoProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 180 180"
      fill="none"
      stroke={color}
      strokeLinejoin="round"
      strokeMiterlimit={2}
      aria-hidden="true"
      style={{ display: 'block', flexShrink: 0 }}
    >
      <g fillRule="evenodd" clipRule="evenodd">
        <path
          d="M107.845,46.92l6.338,-0c10.437,0 18.898,8.461 18.898,18.898l-0,32.432c-0.101,0.109 -0.205,0.216 -0.31,0.322l-6.466,6.466l-0,-39.22c-0,-0.15 -0.003,-0.299 -0.008,-0.447c-0.23,-6.339 -5.327,-11.437 -11.667,-11.667l-6.785,-6.785Zm-35.689,86.161l-6.338,-0c-10.437,-0 -18.898,-8.461 -18.898,-18.898l0,-32.432c0.101,-0.109 0.205,-0.216 0.31,-0.322l6.466,-6.466l0,39.22c0,0.15 0.003,0.299 0.008,0.447c0.23,6.339 5.327,11.437 11.667,11.667l6.785,6.785Zm-25.236,-60.925l-0,-6.338c0,-10.437 8.461,-18.898 18.898,-18.898l32.432,0c0.109,0.101 0.216,0.205 0.322,0.31l6.466,6.466l-39.22,0c-0.15,0 -0.299,0.003 -0.447,0.008c-1.23,0.045 -2.414,0.273 -3.524,0.657c-4.61,1.598 -7.957,5.9 -8.143,11.009l-6.785,6.785Z"
          strokeWidth={3.44}
        />
        <path
          d="M133.08,107.845l0,6.338c-0,10.437 -8.461,18.898 -18.898,18.898l-32.432,-0c-0.109,-0.101 -0.216,-0.205 -0.322,-0.31l-6.466,-6.466l39.22,-0c0.15,-0 0.299,-0.003 0.447,-0.008c6.339,-0.23 11.437,-5.327 11.667,-11.667l6.785,-6.785Z"
          strokeWidth={3.44}
        />
        <path
          d="M107.845,133.08l-4.481,4.481c-7.38,7.38 -19.346,7.38 -26.726,-0l-22.933,-22.933c-0.005,-0.148 -0.008,-0.297 -0.008,-0.447l-0,-9.145l27.733,27.733c0.106,0.106 0.213,0.209 0.322,0.31c4.645,4.32 11.854,4.32 16.499,0l9.595,0Z"
          strokeWidth={3.44}
        />
        <path
          d="M46.92,107.845l-4.481,-4.481c-7.38,-7.38 -7.38,-19.346 0,-26.726l22.933,-22.933c0.148,-0.005 0.297,-0.008 0.447,-0.008l9.145,-0l-27.733,27.733c-0.106,0.106 -0.209,0.213 -0.31,0.322c-4.32,4.645 -4.32,11.854 -0,16.499l-0,9.595Z"
          strokeWidth={3.44}
        />
        <path
          d="M72.155,46.92l4.481,-4.481c7.38,-7.38 19.346,-7.38 26.726,0l22.933,22.933c0.005,0.148 0.008,0.297 0.008,0.447l0,9.145l-27.733,-27.733c-0.106,-0.106 -0.213,-0.209 -0.322,-0.31c-4.645,-4.32 -11.854,-4.32 -16.499,-0l-9.595,0Z"
          strokeWidth={3.44}
        />
        <path
          d="M133.08,72.155l4.481,4.481c7.38,7.38 7.38,19.346 -0,26.726l-22.933,22.933c-0.148,0.005 -0.297,0.008 -0.447,0.008l-9.145,0l27.733,-27.733c0.106,-0.106 0.209,-0.213 0.31,-0.322c4.32,-4.645 4.32,-11.854 0,-16.499l0,-9.595Z"
          strokeWidth={3.44}
        />
        <path
          fill={color}
          stroke="none"
          d="M98.709,91.63l-0.096,-0.043c-0.016,-0.009 -0.444,-0.162 -0.469,-0.171c-0.18,-0.064 -0.383,-0.032 -0.61,0.075l0.009,0.009c-2.657,1.307 -8.804,13.355 -25.297,1.356c-4.3,-3.128 -7.578,-6.898 -12.27,-3.284c-2.855,2.199 3.168,-8.69 10.518,-4.42c8.391,4.875 14.037,18.243 24.813,4.216l0.042,-0.057l0.002,-0.003c1.496,-1.911 3.537,-5.86 3.537,-5.86c0,0 2.361,3.246 2.786,2.915c7.882,-6.134 9.85,-5.45 18.918,-1.181c0.353,0.166 0.341,0.434 -2.734,4.038c-0.985,1.685 -2.442,3.198 -4.15,4.294c-2.824,1.813 -6.334,2.485 -9.527,0.908c-1.214,-0.599 -2.369,-1.311 -3.576,-1.925c0,0 -0.564,-0.279 -0.612,-0.295l-0.099,-0.044c-2.359,2.472 -5.336,5.97 -2.648,1.781c0.61,-0.951 1.089,-1.707 1.46,-2.31Zm17.585,-2.722c-0.909,-0.436 -10.9,-5.227 -12.571,-0.9c-0.486,1.259 11.625,6.025 12.571,0.9Z"
        />
      </g>
    </svg>
  );
}
